import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";

import { FormDialog } from "ui/form-dialog";
import { TextArea } from "ui/text-area";

interface SmsSimulatorRuleModalProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  onSubmit?: (ruleText: string, ruleId?: string) => Promise<void>;
  selectedRule?: {
    id: string;
    description: string;
  } | null;
}

type SmsRuleFormValues = { ruleText: string };

const SmsSimulatorRuleModal = ({
  isOpen,
  setIsOpen,
  onSubmit,
  selectedRule,
}: SmsSimulatorRuleModalProps) => {
  const isEditMode = !!selectedRule;
  const form = useForm<SmsRuleFormValues>({ mode: "onChange", defaultValues: { ruleText: "" } });
  const { control, reset, watch } = form;

  // Reset form when modal opens/closes or selected rule changes
  useEffect(() => {
    if (isOpen) {
      reset({ ruleText: isEditMode && selectedRule ? selectedRule.description : "" });
    }
  }, [isOpen, isEditMode, selectedRule, reset]);

  const handleSubmit = async ({ ruleText }: SmsRuleFormValues) => {
    if (!ruleText.trim() || !onSubmit) return;

    await onSubmit(ruleText, selectedRule?.id);
    setIsOpen(false);
  };

  if (!isOpen) return null;

  return (
    <FormDialog
      open
      onClose={() => setIsOpen(false)}
      headline={isEditMode ? "Edit SMS rule" : "Add a new SMS rule"}
      description={
        isEditMode
          ? "Edit the user-defined rule to improve SMS reply quality."
          : "Please provide the user-defined rule you would like to add to the system model."
      }
      form={form}
      onSubmit={handleSubmit}
      submitDisabled={!watch("ruleText").trim()}
      submitButtonText={isEditMode ? "Update" : "Submit"}
      submitLoadingMessage={isEditMode ? "Updating..." : "Submitting..."}>
      <Controller
        name="ruleText"
        control={control}
        render={({ field }) => (
          <TextArea {...field} label="Rule" placeholder="Add SMS rule here..." required rows={6} />
        )}
      />
    </FormDialog>
  );
};

export default SmsSimulatorRuleModal;
